import NightlightIcon from '@mui/icons-material/Nightlight'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material'
import dayjs from 'dayjs'
import React, {
  ChangeEvent,
  cloneElement,
  CSSProperties,
  useEffect,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import KumocanButton from 'components/button'
import { Calendar, CalendarProps, Schedule, Task } from 'components/Calendar'
import KumocanDialog from 'components/dialog'
import { notificationApi } from 'hooks/api/notification'
import { shiftApi } from 'hooks/api/shift'
import { isFuture, NewDate } from 'hooks/helper/date'
import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'
import { ASSIGN_STATUS, AssignWithOrder } from 'types/assign'
import { OrderWithContractDetail } from 'types/order'
import { Shift } from 'types/shift'

import ListCalendar, { ListCalendarProps } from './listCalendarComponent'
import TabComponent, { Tab } from './shiftTabComponent'
import SubmitShift from './submitShiftComponent'

export default function ShiftPage(): JSX.Element {
  const { t } = useTranslation(['page-shift', 'page-assign-detail', 'common'])
  const { showSnackbar } = useSnackbar()
  const [selectTab, onSelectTab] = useState<Tab>(Tab.LIST)
  // カレンダー表示に使用する現在の日付
  const [currentDate, setCurrentDate] = useState<Date | undefined>(undefined)
  // 直近のスケジュールを表示するための日付
  const [latestDate, setLatestDate] = useState<Date | undefined>(undefined)
  const [schedule, setSchedule] = useState<Schedule | undefined>(undefined)
  const [assign, setAssign] = useState<AssignWithOrder | undefined>(undefined)
  const [open, setOpen] = useState<boolean>(false)
  const [res, setRes] = useState<number>(0)
  const [replyWhenUnavailable, setReplyWhenUnavailable] = useState<string>('')
  const [replyWhenUnavailableErr, setReplyWhenUnavailableErr] =
    useState<string>('')
  const [replyFormat, setReplyFormat] = useState<string>()
  const [replyWhenAvailable, setReplyWhenAvailable] = useState<string>('')
  const [shifts, setShifts] = useState<Shift[]>([])
  const [paidLeaveCount, setPaidLeaveCount] = useState<number>(0)

  const fetchData = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    shiftApi()
      .findByDate(start, end)
      .then((res) => {
        setShifts(res.data.shifts)
      })

    // 有給取得数の取得（簡易実装）
    setPaidLeaveCount(20) // デフォルト値として設定、実際はAPIから取得
  }

  useEffect(() => {
    const start = dayjs().startOf('month')
    const end = dayjs().endOf('month')
    fetchData(start, end)
  }, [])

  const scheduleColor = (schedule: number): string => {
    switch (schedule) {
      case 1:
        return 'rgb(0, 0, 0, 0.1)'
      case 2:
        return 'rgb(255, 72, 0, 0.5)'
      case 3:
        return 'rgb(0, 98, 140, 0.5)'
      case 4:
        return 'rgb(223, 173, 104, 0.5)'
      case 5:
        return 'rgb(137, 211, 188, 0.5)'
      case 6:
        return 'rgb(165, 160, 207, 0.5)'
      case 7:
        return 'rgb(243, 161, 178, 0.5)'
      case 8:
        return '#FFC5B9'
      case 9:
        return '#FFA896'
      default:
        return 'white'
    }
  }

  const calendarIconStyle: CSSProperties = {
    display: 'block',
    fontSize: '12px',
    color: 'rgb(255, 255, 255, 0.5)',
  }

  const scheduleIcon = (schedule: number): React.ReactNode | undefined => {
    if (schedule === 2) {
      return <WbSunnyIcon style={calendarIconStyle} />
    } else if (schedule === 3) {
      return <NightlightIcon style={calendarIconStyle} />
    } else if (schedule === 8) {
      return <WbSunnyIcon style={calendarIconStyle} />
    } else if (schedule === 9) {
      return <NightlightIcon style={calendarIconStyle} />
    }
    return undefined
  }

  // 予定稼働時間の表示文字列。終了≦開始は翌日扱い（例: 21:00〜翌6:00）
  const formatScheduledTime = (shift: Shift): string | undefined => {
    if (!shift.scheduledStartTime || !shift.scheduledEndTime) return undefined
    const toDisplay = (v: string) => {
      const [h, m] = v.split(':')
      return `${Number(h)}:${m}`
    }
    const key =
      shift.scheduledEndTime <= shift.scheduledStartTime
        ? 'scheduled_time.range_next_day'
        : 'scheduled_time.range'
    return t(key, {
      start: toDisplay(shift.scheduledStartTime),
      end: toDisplay(shift.scheduledEndTime),
    })
  }

  const schedules = shifts.map((shift): Schedule => {
    const dayShiftAssign = shift.assigns.find((assign) => {
      return assign.order.shiftType === 1
    })
    const nightShiftAssign = shift.assigns.find((assign) => {
      return assign.order.shiftType === 2
    })
    const isAssigned = shift.assigns.length > 0
    const dayShiftTask = dayShiftAssign
      ? ({
          assignId: dayShiftAssign.id,
          time: `${NewDate(dayShiftAssign.scheduledStartTime).format('HH:mm')} 〜 ${NewDate(dayShiftAssign.scheduledEndTime).format('HH:mm')}`,
          name: `${dayShiftAssign.order.contractDetail.name} | ${dayShiftAssign.order.address}`,
          color:
            dayShiftAssign.status > 20
              ? 'rgb(255, 163, 128)'
              : 'rgb(255, 163, 128, 0.5)',
          icon: <span>{t('shift_types.day_shift')}</span>,
          status: dayShiftAssign.status,
        } as Task)
      : shift.schedule === 2 && !isAssigned
        ? ({
            assignId: '',
            time: '',
            name: '',
            color: 'rgb(255, 72, 0, 0.5)',
            icon: <span>{t('shift_types.day_shift')}</span>,
            status: -1,
          } as Task)
        : undefined
    const nightShiftTask = nightShiftAssign
      ? ({
          assignId: nightShiftAssign.id,
          time: `${NewDate(nightShiftAssign.scheduledStartTime).format('HH:mm')} 〜 ${NewDate(nightShiftAssign.scheduledEndTime).format('HH:mm')}`,
          name: `${nightShiftAssign.order.contractDetail.name} | ${nightShiftAssign.order.address}`,
          color:
            nightShiftAssign.status > 20
              ? 'rgb(0, 98, 140)'
              : 'rgb(0, 98, 140, 0.5)',
          icon: <span>{t('shift_types.night_shift')}</span>,
          status: nightShiftAssign.status,
        } as Task)
      : shift.schedule === 3 && !isAssigned
        ? ({
            assignId: '',
            time: '',
            name: '',
            color: 'rgb(0, 98, 140, 0.5)',
            icon: <span>{t('shift_types.night_shift')}</span>,
            status: -1,
          } as Task)
        : undefined
    const amShiftTask =
      shift.schedule === 8 && !isAssigned
        ? ({
            assignId: '',
            time: '',
            name: '',
            color: '#FFC5B9',
            icon: <span>{t('shift_types.morning')}</span>,
            status: -1,
          } as Task)
        : undefined
    const pmShiftTask =
      shift.schedule === 9 && !isAssigned
        ? ({
            assignId: '',
            time: '',
            name: '',
            color: '#FFA896',
            icon: <span>{t('shift_types.afternoon')}</span>,
            status: -1,
          } as Task)
        : undefined
    const dayOrNightShiftTask =
      shift.schedule === 6 && !dayShiftAssign && !nightShiftAssign
        ? ({
            assignId: '',
            time: '',
            name: '',
            color: 'rgb(165, 160, 207)',
            icon: <span>{t('shift_types.day_or_night')}</span>,
            status: -1,
          } as Task)
        : undefined
    const trainingShiftTask =
      shift.schedule === 7 && !dayShiftAssign && !nightShiftAssign
        ? ({
            assignId: '',
            time: '',
            name: '',
            color: 'rgb(243, 161, 178)',
            icon: <span>{t('shift_types.training')}</span>,
            status: -1,
          } as Task)
        : undefined
    const dayAndNightShiftTask =
      shift.schedule === 4 && !dayShiftAssign && !nightShiftAssign
        ? ({
            assignId: '',
            time: '',
            name: '',
            color: 'rgb(223, 173, 104)',
            icon: <span>{t('shift_types.day_night_available')}</span>,
            status: -1,
          } as Task)
        : undefined
    const reviewShiftTask =
      shift.schedule === 5 && !dayShiftAssign && !nightShiftAssign
        ? ({
            assignId: '',
            time: '',
            name: '',
            color: 'rgb(137, 211, 188)',
            icon: <span>{t('shift_types.reviewing')}</span>,
            status: -1,
          } as Task)
        : undefined
    const assignLength = [
      dayShiftTask,
      nightShiftTask,
      amShiftTask,
      pmShiftTask,
      dayOrNightShiftTask,
      trainingShiftTask,
      dayAndNightShiftTask,
      reviewShiftTask,
    ].filter((task) => task !== undefined).length
    return {
      date: NewDate(shift.date).toDate(),
      color: scheduleColor(shift.schedule),
      icon: scheduleIcon(shift.schedule),
      dayShiftTask: dayShiftTask,
      nightShiftTask: nightShiftTask,
      amShiftTask: amShiftTask,
      pmShiftTask: pmShiftTask,
      dayOrNightShiftTask: dayOrNightShiftTask,
      trainingShiftTask: trainingShiftTask,
      dayAndNightShiftTask: dayAndNightShiftTask,
      reviewShiftTask: reviewShiftTask,
      assignLength: assignLength,
      scheduledTimeLabel: formatScheduledTime(shift),
      // その日に警備先へのアサインが1件でもある場合は予定稼働時間の表示が冗長になるため判定に使用
      hasAssign: isAssigned,
    }
  })

  const props: CalendarProps = {
    date: currentDate ?? new Date(),
    schedules: schedules,
    onClick: (schedule) => {
      setCurrentDate(schedule?.date)
      setSchedule(schedule)
    },
    changeMonth: (date) => {
      setCurrentDate(date)
      const start = dayjs(date).startOf('month')
      const end = dayjs(date).endOf('month')

      shiftApi()
        .findByDate(start, end)
        .then((res) => {
          setShifts(res.data.shifts)
        })
    },
  }

  const listCalendarProps: ListCalendarProps = {
    currentDate: latestDate ?? new Date(),
    schedules: schedules,
    onSelectTask: (schedule: Schedule, task: Task) => {
      setSchedule(schedule)
      showModal(task.assignId)
    },
    changeDate: (date: Date) => {
      const start = dayjs(date).subtract(1, 'day')
      const end = dayjs(date).add(3, 'day') // APIの関係上endの日付は表示日数よりも1つ大きくする必要がある
      setCurrentDate(date)
      setLatestDate(date)
      fetchData(start, end)
    },
  }

  const showModal = (assignId: string) => {
    const assign = shifts
      .flatMap((shift) => shift.assigns)
      .find((assign) => assign.id === assignId)
    setAssign(assign)
    setReplyFormat(assign?.replyWhenAvailable ?? '')
    setReplyWhenUnavailable(assign?.replyWhenUnavailable ?? '')
    if (assign?.replyWhenAvailable) {
      setRes(ASSIGN_STATUS.AVAILABLE_FOR_WORK)
    } else if (assign?.replyWhenUnavailable) {
      setRes(ASSIGN_STATUS.UNAVAILABLE_FOR_WORK)
    }
    if (!assign) return
    setOpen(true)
  }

  const closeModal = () => {
    setOpen(false)
  }

  const handleResChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReplyWhenUnavailable('')
    setReplyWhenAvailable('')
    setRes(Number((event.target as HTMLInputElement).value))
  }

  const isDisabled = (): boolean => {
    return res === 0
  }

  const onSubmit = (notificationId: string) => {
    if (
      res === ASSIGN_STATUS.UNAVAILABLE_FOR_WORK &&
      replyWhenUnavailable.trim() === ''
    ) {
      setReplyWhenUnavailableErr(t('report_required'))
      return
    }
    notificationApi()
      .replyAssignPossibility(
        notificationId,
        res,
        replyWhenAvailable,
        replyWhenUnavailable,
      )
      .then(() => {
        const start = dayjs().startOf('month')
        const end = dayjs().endOf('month')
        fetchData(start, end)
        // 報告を行った場合配置が取り消されるので、表示データを更新する必要がある
        const schedule = schedules.find(
          (schedule) => schedule.date === currentDate,
        )
        setSchedule(schedule)
        closeModal()
      })
  }

  const toMap = (order: OrderWithContractDetail) => {
    if (!order.address) {
      showSnackbar(t('common:messages.address_not_registered'))
    } else if (order.lat !== 0 && order.lng !== 0) {
      // 緯度経度が存在する場合、緯度経度でGoogleマップを開く
      location.href = `https://www.google.com/maps/search/?api=1&query=${order.lat},${order.lng}&launch_app=true`
    } else {
      // 緯度経度が0の場合は住所でGoogleマップを開く
      location.href = `https://www.google.com/maps/search/?api=1&query=${order.address}&launch_app=true`
    }
  }

  const handleReplyWhenUnavailable = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const data = event.target.value.trim()
    setReplyWhenUnavailable(data)
  }

  const handleReplyChange = (event: ChangeEvent<HTMLInputElement>) => {
    const data = event.target.value.trim()
    setReplyWhenAvailable(data)
  }

  const changeTab = (tab: Tab) => {
    // カレンダー表示の場合は月間のスケジュールを取得
    if (tab === Tab.CALENDAR) {
      const start = dayjs(currentDate).startOf('month')
      const end = dayjs(currentDate).endOf('month')
      fetchData(start, end)
    } else if (tab === Tab.LIST) {
      // リスト形式のカレンダーの場合は直近のスケジュールを取得 (4日分)
      const start = dayjs(latestDate).subtract(1, 'day')
      const end = dayjs(latestDate).add(3, 'day')
      fetchData(start, end)
    }
    onSelectTab(tab)
  }

  return (
    <Box>
      <Box style={{ marginTop: '40px' }}>
        <TabComponent activeTab={selectTab} onSelectTab={changeTab} />
      </Box>

      {selectTab === Tab.CALENDAR && (
        <Box>
          <Calendar {...props} />
          <Box style={scheduleStyle}>
            {schedule && (
              <Box>
                <h2 style={{ fontSize: '16px' }}>
                  {NewDate(schedule.date).format(
                    t('common:date_format.date_with_day'),
                  )}
                </h2>
                {schedule.scheduledTimeLabel && !schedule.hasAssign && (
                  <p style={{ fontSize: '14px', marginTop: '4px' }}>
                    {t('scheduled_time.label')} {schedule.scheduledTimeLabel}
                  </p>
                )}
                <ul style={tasksStyle}>
                  {[schedule.dayShiftTask, schedule.nightShiftTask]
                    .filter((task) => task?.status !== -1)
                    .map((task, index) => {
                      if (task) {
                        return (
                          <li
                            key={task.name + '-' + index}
                            style={taskStyle(isFuture(schedule.date), task)}
                            onClick={() => showModal(task.assignId)}
                          >
                            <div>
                              <p
                                style={{ fontSize: '18px', fontWeight: 'bold' }}
                              >
                                {task.time}
                              </p>
                              <p style={{ fontSize: '12px' }}>{task.name}</p>
                            </div>
                            {task.icon &&
                              cloneElement(
                                task.icon as React.ReactElement<any>,
                                {
                                  style: {
                                    fontSize: '24px',
                                    color: 'rgba(255, 255, 255, 0.5)',
                                  },
                                },
                              )}
                          </li>
                        )
                      } else {
                        return null
                      }
                    })}
                </ul>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {selectTab === Tab.LIST && <ListCalendar {...listCalendarProps} />}
      {selectTab === Tab.SUBMIT && (
        <Box>
          <SubmitShift paidLeaveCount={paidLeaveCount} />
        </Box>
      )}
      <KumocanDialog open={open} handleClose={closeModal} maxWidth="lg">
        <Box>
          {schedule && assign && (
            <Box>
              <div style={flatRowStyle}>
                {NewDate(schedule.date).format(
                  t('common:date_format.date_with_day'),
                )}{' '}
                {NewDate(assign.scheduledStartTime).format('HH:mm')}-
                {NewDate(assign.scheduledEndTime).format('HH:mm')}
              </div>
              {schedule.scheduledTimeLabel && !schedule.hasAssign && (
                <div style={rowStyle}>
                  <p style={labelStyle}>{t('scheduled_time.label')}</p>
                  <p style={contentStyle}>{schedule.scheduledTimeLabel}</p>
                </div>
              )}
              <div style={rowStyle}>
                <p style={labelStyle}>{t('page-assign-detail:client_name')}</p>
                <p style={contentStyle}>
                  {assign.order.contractDetail.contract?.client?.name}
                </p>
              </div>
              <div style={rowStyle}>
                <p style={labelStyle}>
                  {t('page-assign-detail:contract_name')}
                </p>
                <p style={contentStyle}>{assign.order.contractDetail.name}</p>
              </div>
              <div style={rowStyle}>
                <p style={labelStyle}>
                  {t('page-assign-detail:nearest_station')}
                </p>
                <p style={contentStyle}>
                  {assign.order.contractDetail.nearestStation}
                </p>
              </div>
              <div style={rowStyle}>
                <p style={labelStyle}>{t('page-assign-detail:address')}</p>
                <div>
                  <p style={contentStyle}>{assign.order.address}</p>
                  <div style={{ marginTop: '10px', textAlign: 'left' }}>
                    {' '}
                    <KumocanButton onClick={() => toMap(assign.order)}>
                      {t('common:buttons.show_map')}
                    </KumocanButton>
                  </div>
                </div>
              </div>
              {assign.securityNote && (
                <div style={rowStyle}>
                  <p style={labelStyle}>{t('page-shift:security_note')}</p>
                  <p style={contentStyle}>{assign.securityNote}</p>
                </div>
              )}
              {assign.members && (
                <div style={rowStyle}>
                  <p style={labelStyle}>{t('page-assign-detail:workers')}</p>
                  <div style={multiContentStyle}>
                    {assign.members.map((member, index) => {
                      return (
                        <span
                          key={member.security.id + '-' + index}
                          style={tagStyle}
                        >
                          {member.security.lastName} {member.security.firstName}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}
              {assign.status === 20 &&
                assign.notification &&
                assign.notification?.id !== '' && (
                  <Box>
                    <div style={flatRowStyle}>
                      <p
                        style={{
                          ...labelStyle,
                          width: '100%',
                          paddingBottom: '8px',
                        }}
                      >
                        {t('page-assign-detail:comment_to_security')}
                      </p>
                      <p>{assign.notification.content}</p>
                    </div>

                    <FormControl style={{ width: '100%' }}>
                      <RadioGroup
                        aria-labelledby="demo-controlled-radio-buttons-group"
                        name="controlled-radio-buttons-group"
                        value={res}
                        onChange={handleResChange}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          gap: '20px',
                          marginBottom: '10px',
                        }}
                      >
                        <FormControlLabel
                          value={ASSIGN_STATUS.AVAILABLE_FOR_WORK}
                          control={
                            <Radio
                              sx={{
                                color: '#cccccc',
                                '&.Mui-checked': {
                                  color: '#14b7ae',
                                },
                              }}
                            />
                          }
                          label={t('common:response.confirmed')}
                          sx={{ fontSize: '16px' }}
                        />

                        <FormControlLabel
                          value={ASSIGN_STATUS.UNAVAILABLE_FOR_WORK}
                          control={
                            <Radio
                              sx={{
                                color: '#cccccc',
                                '&.Mui-checked': {
                                  color: '#14b7ae',
                                },
                              }}
                            />
                          }
                          label={t('common:response.report')}
                          sx={{ fontSize: '16px' }}
                        />
                      </RadioGroup>

                      {res === ASSIGN_STATUS.UNAVAILABLE_FOR_WORK && (
                        <TextField
                          sx={{ marginTop: '8px', marginBottom: '8px' }}
                          id="outlined-multiline-flexible"
                          label={t('common:response.report_content')}
                          multiline
                          maxRows={5}
                          required
                          onChange={handleReplyWhenUnavailable}
                          error={!!replyWhenUnavailableErr.trim()}
                        />
                      )}

                      {res === ASSIGN_STATUS.AVAILABLE_FOR_WORK && (
                        <TextField
                          label={t('common:response.reply_comment')}
                          sx={{ marginTop: '8px', marginBottom: '8px' }}
                          id="outlined-multiline-flexible"
                          multiline
                          defaultValue={replyFormat}
                          maxRows={5}
                          onChange={handleReplyChange}
                        />
                      )}

                      <KumocanButton
                        onClick={() => onSubmit(assign.notification!.id)}
                        style={submitButtonStyle}
                        disabled={isDisabled()}
                      >
                        {t('common:buttons.submit_answer')}
                      </KumocanButton>
                    </FormControl>
                  </Box>
                )}
            </Box>
          )}
        </Box>
      </KumocanDialog>
    </Box>
  )
}

const scheduleStyle: CSSProperties = {
  padding: '8px',
}

const tasksStyle: CSSProperties = {
  marginTop: '8px',
  display: 'flex',
  flexFlow: 'column',
  gap: '8px',
}

const taskStyle = (isFuture: boolean, task: Task): CSSProperties => {
  const color = isFuture ? task.color : '#d1d5db'
  const borderColor = task.status === 20 ? 'red' : color
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    borderRadius: '8px',
    backgroundColor: color,
    padding: '8px',
    border: `2px solid ${borderColor}`,
    color: 'white',
  }
}

const flatRowStyle: CSSProperties = {
  paddingBottom: '10px',
  marginBottom: '16px',
  fontSize: '14px',
  fontWeight: 'bold',
  borderBottom: '2px dotted #eee',
}

const rowStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  marginBottom: '16px',
  borderBottom: '2px dotted #eee',
  paddingBottom: '10px',
}

const labelStyle: CSSProperties = {
  width: '28%',
  fontWeight: 'bold',
  color: '#555',
  fontSize: '14px',
}

const contentStyle: CSSProperties = {
  flex: 1,
}

const multiContentStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  width: '75%',
  gap: '8px',
}

const tagStyle: CSSProperties = {
  backgroundColor: '#e0e0e0',
  padding: '5px 10px',
  borderRadius: '4px',
  fontSize: '14px',
}

const submitButtonStyle: CSSProperties = {
  height: '50px',
  fontSize: '18px',
  borderRadius: '4px',
}
